import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Accreditations from "@/components/site/Accreditations";
import CTASection from "@/components/site/CTASection";
import SEO from "@/components/site/SEO";
import { SERVICES } from "@/lib/site";

export default function Services() {
  return (
    <>
      <SEO
        title="Services — L F Building & Maintenance Ltd"
        description="Full residential builds, extensions, roofing, loft conversions, kitchens and bathrooms — delivered across Essex, Suffolk and surrounding areas."
        path="/services"
      />

      {/* HEADER */}
      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-20 md:pb-28 border-b border-white/10 grid-lines">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <span className="eyebrow text-brand">Services</span>
            <h1 className="font-display font-bold uppercase text-6xl md:text-8xl leading-[0.9]">
              What we <span className="text-brand">build.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              Six specialist disciplines — one accountable team. Every service is delivered with in-house tradespeople,
              a named site manager and a 10-year workmanship guarantee.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICE ROWS */}
      <section className="bg-white" data-testid="services-list">
        {SERVICES.map((s, i) => {
          const reverse = i % 2 === 1;
          return (
            <div
              key={s.slug}
              data-testid={`service-row-${s.slug}`}
              className={`border-b border-black/10 py-20 md:py-28`}
            >
              <div className={`container-lf grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-6 relative">
                  <div className="aspect-[4/3] overflow-hidden border border-black/10">
                    <img src={s.image} alt={s.name} className={`w-full h-full object-cover hover:scale-105 transition-transform duration-700 ${s.imageRotate ? "rotate-90 scale-[1.34] hover:scale-[1.4]" : ""}`} loading="lazy" />
                  </div>
                  <div className="absolute -top-4 -left-4 bg-brand text-white w-16 h-16 grid place-items-center font-display font-bold text-2xl hidden md:grid">
                    0{i + 1}
                  </div>
                </div>
                <div className="lg:col-span-6 space-y-6">
                  <span className="eyebrow">Service 0{i + 1}</span>
                  <h2 className="font-display font-bold uppercase text-5xl md:text-6xl leading-[0.95]">
                    {s.name}
                  </h2>
                  <p className="text-xl text-brand font-semibold">{s.tagline}</p>
                  <p className="text-neutral-700 leading-relaxed">{s.description}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm">
                        <Check className="w-5 h-5 text-brand shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4">
                    <Link to="/contact" data-testid={`service-cta-${s.slug}`}>
                      <Button className="rounded-none bg-brand-ink hover:bg-brand text-white h-12 px-6 uppercase tracking-wider text-sm font-semibold">
                        Enquire about {s.name} <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <Accreditations variant="dark" />
      <CTASection eyebrow="Have a project in mind?" title="Let's price it up." />
    </>
  );
}
